import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type {
  EventsOrganization,
  EventsScope,
  EventsSession,
  EventsVenue,
} from './api/types';

type EventsScopeValue = {
  organizations: EventsOrganization[];
  organization: EventsOrganization;
  venue: EventsVenue | null;
  scope: EventsScope;
  /** "All venues" is offered only when the organization has several venues. */
  allVenuesAllowed: boolean;
  selectOrganization: (organizationId: number) => void;
  selectVenue: (venueId: number | null) => void;
};

const EventsScopeContext = createContext<EventsScopeValue | null>(null);

function defaultScopeFor(organization: EventsOrganization): EventsScope {
  // A listed venue always resolves a role; the organization level may not.
  return {
    organizationId: organization.id,
    venueId: organization.venues[0]?.id ?? null,
  };
}

function allowsAllVenues(organization: EventsOrganization): boolean {
  return organization.venues.length !== 1;
}

/** Keep only a scope the organizations response actually grants. */
export function reconcileScope(
  organizations: EventsOrganization[],
  scope: EventsScope | null,
): EventsScope {
  const organization =
    organizations.find((org) => org.id === scope?.organizationId) ??
    organizations[0];
  if (!scope || organization.id !== scope.organizationId) {
    return defaultScopeFor(organization);
  }
  if (scope.venueId === null) {
    return allowsAllVenues(organization)
      ? scope
      : defaultScopeFor(organization);
  }
  return organization.venues.some((venue) => venue.id === scope.venueId)
    ? scope
    : defaultScopeFor(organization);
}

export function EventsScopeProvider({
  organizations,
  children,
}: {
  organizations: EventsOrganization[];
  children: React.ReactNode;
}) {
  const [requested, setRequested] = useState<EventsScope | null>(null);
  const scope = useMemo(
    () => reconcileScope(organizations, requested),
    [organizations, requested],
  );

  const organization = organizations.find(
    (org) => org.id === scope.organizationId,
  )!;
  const venue =
    scope.venueId === null
      ? null
      : (organization.venues.find((v) => v.id === scope.venueId) ?? null);

  const selectOrganization = useCallback(
    (organizationId: number) => {
      const next = organizations.find((org) => org.id === organizationId);
      if (next) setRequested(defaultScopeFor(next));
    },
    [organizations],
  );

  const selectVenue = useCallback(
    (venueId: number | null) => {
      setRequested(
        reconcileScope(organizations, {
          organizationId: scope.organizationId,
          venueId,
        }),
      );
    },
    [organizations, scope.organizationId],
  );

  const value = useMemo(
    () => ({
      organizations,
      organization,
      venue,
      scope,
      allVenuesAllowed: allowsAllVenues(organization),
      selectOrganization,
      selectVenue,
    }),
    [
      organizations,
      organization,
      venue,
      scope,
      selectOrganization,
      selectVenue,
    ],
  );

  return (
    <EventsScopeContext.Provider value={value}>
      {children}
    </EventsScopeContext.Provider>
  );
}

export function useEventsScope(): EventsScopeValue {
  const value = useContext(EventsScopeContext);
  if (!value) {
    throw new Error('useEventsScope must be used inside EventsScopeProvider');
  }
  return value;
}

const EventsSessionContext = createContext<EventsSession | null>(null);

export const EventsSessionProvider = EventsSessionContext.Provider;

/** The loaded, server-authoritative session for the current scope. */
export function useEventsSession(): EventsSession {
  const session = useContext(EventsSessionContext);
  if (!session) {
    throw new Error('useEventsSession must be used inside the events layout');
  }
  return session;
}
